/**
 * The receivables position: source facts read through a company's settings
 * (docs/tally/18 §1–§6).
 *
 * ## What it shows, and why in this shape
 *
 * Three figures, always together, never one hidden behind another:
 *
 *   Owed to you         — the balances of the accounts in debit;
 *   Held for customers  — the balances of the accounts in credit;
 *   Net receivable      — the difference: TallyPrime's Trial Balance for
 *                         Sundry Debtors, debit column less credit column.
 *
 * And how the net is made up, bill by bill:
 *
 *   open bills on the debit side      aged, by the company's buckets
 * + debit money in no bill            unaged: not kept bill-by-bill, or on account
 * − open bills on the credit side     credit notes as new references, advances
 * − credit money in no bill           received On Account, kept in no bill
 * = net receivable
 *
 * The ageing is **the debit bills alone**, gross — what TallyPrime's own
 * Age-wise report shows (proved by hand against it on 1 October 2026,
 * docs/tally/11). A credit is never netted into the bucket of its own date:
 * a credit note dated last week is not "money 0–45 days old", and an advance
 * says nothing about which invoice it will settle. Credits are listed apart,
 * as TallyPrime lists them (in Bills Payable), and they reduce the net.
 *
 * The book's own columns (`ageing`, `over90`, `dueOver45` on each account)
 * are unchanged by all of this: `normalizeTallyExtract` still writes them
 * exactly as before, for Customers, Today and Reports.
 *
 * ## Invariants (held by tests)
 *
 *   - per account: closing = Σ debit bills − Σ credit bills + unbilled;
 *   - book: Σ closing (in scope) = owed − held = net = the composition;
 *   - every debit bill is placed exactly once: buckets + not yet due +
 *     unaged for want of a due date = Σ debit bills;
 *   - no setting changes a closing balance, a bill's dates or its age.
 */
import {
    ageBill, bucketLabels,
    type AgeingConfig, type BillPlacement,
} from './ageingEngine.js';
import {
    addDays, daysFrom, fromPaise, toPaise,
    type SourceAccountFact, type SourceBillFact, type SourceFacts, type UnbilledReason,
} from './receivablesFacts.js';
import type { ReceivablesSettings, ScopeRules } from './receivablesSettings.js';

export interface BillView extends SourceBillFact {
    side: 'Dr' | 'Cr';
    /** Days since the bill date. A fact from the dates; the same under every setting. */
    ageDays: number;
    /** Days past TallyPrime's due date; null when it gave none. */
    overdueDays: number | null;
    /** Debit bills only: where the company's ageing puts it. */
    placement?: BillPlacement;
    bucketLabel?: string;
    /**
     * Debit bills only. The first day Timely treats the bill as overdue for
     * collections: the day after its due date plus grace. TallyPrime's due
     * date is unchanged and shown beside it. Null without a usable due date.
     */
    collectionOverdueFrom: string | null;
    overdueForCollections: boolean;
    withinGrace: boolean;
}

export type ExclusionRule = { kind: 'group'; group: string } | { kind: 'account' };

export interface AccountPosition {
    sourceKey: string;
    name: string;
    group: string;
    billWise: boolean;
    inScope: boolean;
    excludedBy?: ExclusionRule;
    /** TallyPrime's closing balance, signed (+ = Dr). */
    closing: number;
    /** Σ open debit bills. */
    debitBills: number;
    /** Σ open credit bills, as a positive amount. */
    creditBills: number;
    /** Money in the balance in no bill, signed, with TallyPrime's reason. */
    unbilled: number;
    unbilledReason: UnbilledReason | null;
    /** Debit bills per bucket of the company's layout. */
    buckets: number[];
    notYetDue: number;
    unagedNoDueDate: number;
    overdueForCollections: number;
    withinGrace: number;
    /**
     * The most days any debit bill is past its due date — TallyPrime's, or the
     * bill date where it gave none and the company reads that as due then.
     * Null: none is.
     */
    oldestOverdueDays: number | null;
    bills: BillView[];
}

export interface PositionSummary {
    accounts: { read: number; withBalance: number; inScope: number; excluded: number; inDebit: number; inCredit: number };
    owedToYou: number;
    heldForCustomers: number;
    net: number;
    composition: {
        debitBills: number;
        debitNotBillWise: number;
        debitOnAccount: number;
        /** Positive amounts; they reduce the net. */
        creditBills: number;
        creditOnAccount: number;
        creditNotBillWise: number;
    };
    ageing: {
        basis: AgeingConfig['basis'];
        labels: string[];
        buckets: { label: string; amount: number; bills: number }[];
        notYetDue: { amount: number; bills: number };
        unagedNoDueDate: { amount: number; bills: number };
        /** Σ debit bills: the total the ageing covers. */
        aged: { amount: number; bills: number };
    };
    /** Money in the net that no bucket covers, by TallyPrime's reason. Signed. */
    unaged: { notBillWise: number; onAccount: number; total: number };
    overdueForCollections: { amount: number; bills: number };
    withinGrace: { amount: number; bills: number };
    graceDays: number;
    excluded: {
        accounts: number;
        net: number;
        byGroup: { group: string; accounts: number; net: number }[];
        byAccount: { sourceKey: string; name: string; net: number }[];
    };
    unmatchedBills: { bills: number; amount: number };
    bills: { debit: number; credit: number };
}

export interface ReceivablesPosition {
    asOf: string;
    accounts: AccountPosition[];
    summary: PositionSummary;
}

/** Whether a rule excludes this account, and which. Group names match as TallyPrime spells them, ignoring case. */
export function exclusionFor(account: Pick<SourceAccountFact, 'sourceKey' | 'group'>, scope: ScopeRules): ExclusionRule | undefined {
    if (scope.excludeAccounts.some(k => k.toLowerCase() === account.sourceKey.toLowerCase())) return { kind: 'account' };
    const g = scope.excludeGroups.find(x => x.trim().toLowerCase() === account.group.trim().toLowerCase());
    return g ? { kind: 'group', group: g } : undefined;
}

/** The day a debit bill becomes overdue for collections, from TallyPrime's due date and the grace. */
export function collectionOverdueFrom(bill: { billDate: string; dueDate?: string }, settings: Pick<ReceivablesSettings, 'graceDays' | 'ageing'>): string | null {
    const due = bill.dueDate ?? (settings.ageing.missingDueDate === 'use_bill_date' ? bill.billDate : null);
    return due ? addDays(due, settings.graceDays + 1) : null;
}

export function interpretReceivables(facts: Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills'>, settings: ReceivablesSettings): ReceivablesPosition {
    const labels = bucketLabels(settings.ageing);
    const asOf = facts.asOf;

    const accounts: AccountPosition[] = facts.accounts.map(account => {
        const excludedBy = exclusionFor(account, settings.scope);
        const buckets = labels.map(() => 0);
        let debitP = 0, creditP = 0, notYetDueP = 0, unagedP = 0, overdueP = 0, graceP = 0;
        let oldest: number | null = null;
        const bills: BillView[] = account.bills.map(bill => {
            const p = toPaise(bill.pending);
            const side: 'Dr' | 'Cr' = p < 0 ? 'Cr' : 'Dr';
            const ageDays = daysFrom(bill.billDate, asOf);
            const overdueDays = bill.dueDate ? daysFrom(bill.dueDate, asOf) : null;
            if (side === 'Cr') {
                creditP += -p;
                return { ...bill, side, ageDays, overdueDays, collectionOverdueFrom: null, overdueForCollections: false, withinGrace: false };
            }
            debitP += p;
            const aged = ageBill(bill, asOf, settings.ageing);
            let bucketLabel: string | undefined;
            if (aged.placement.kind === 'bucket') { buckets[aged.placement.bucket] = (buckets[aged.placement.bucket] ?? 0) + p; bucketLabel = labels[aged.placement.bucket]; }
            else if (aged.placement.kind === 'not_yet_due') notYetDueP += p;
            else unagedP += p;
            const from = collectionOverdueFrom(bill, settings);
            const overdueForCollections = from !== null && asOf >= from;
            // Past due by TallyPrime's due date — or, where it gave none and the company reads that as due on the bill date, by the bill date.
            const effectiveOverdue = overdueDays ?? (settings.ageing.missingDueDate === 'use_bill_date' ? ageDays : null);
            const withinGrace = effectiveOverdue !== null && effectiveOverdue > 0 && !overdueForCollections;
            if (overdueForCollections) overdueP += p;
            if (withinGrace) graceP += p;
            if (effectiveOverdue !== null && effectiveOverdue > 0) oldest = Math.max(oldest ?? 0, effectiveOverdue);
            return {
                ...bill, side, ageDays, overdueDays, placement: aged.placement,
                ...(bucketLabel ? { bucketLabel } : {}),
                collectionOverdueFrom: from, overdueForCollections, withinGrace,
            };
        });
        return {
            sourceKey: account.sourceKey,
            name: account.name,
            group: account.group,
            billWise: account.billWise,
            inScope: !excludedBy,
            ...(excludedBy ? { excludedBy } : {}),
            closing: account.closing,
            debitBills: fromPaise(debitP),
            creditBills: fromPaise(creditP),
            unbilled: account.unbilled,
            unbilledReason: account.unbilledReason,
            buckets: buckets.map(fromPaise),
            notYetDue: fromPaise(notYetDueP),
            unagedNoDueDate: fromPaise(unagedP),
            overdueForCollections: fromPaise(overdueP),
            withinGrace: fromPaise(graceP),
            oldestOverdueDays: oldest,
            bills,
        };
    });

    return { asOf, accounts, summary: summarizePosition(accounts, facts.unmatchedBills, settings, labels) };
}

function summarizePosition(
    accounts: readonly AccountPosition[], unmatched: SourceFacts['unmatchedBills'], settings: ReceivablesSettings, labels: string[],
): PositionSummary {
    const inScope = accounts.filter(a => a.inScope);
    const excluded = accounts.filter(a => !a.inScope);
    let owed = 0, held = 0, inDebit = 0, inCredit = 0, withBalance = 0;
    let dBills = 0, dNotBillWise = 0, dOnAccount = 0, cBills = 0, cOnAccount = 0, cNotBillWise = 0;
    const bucketP = labels.map(() => 0), bucketN = labels.map(() => 0);
    let nyd = 0, nydN = 0, unagedNoDue = 0, unagedNoDueN = 0, agedN = 0;
    let overdue = 0, overdueN = 0, grace = 0, graceN = 0, debitN = 0, creditN = 0;
    for (const a of inScope) {
        const c = toPaise(a.closing);
        if (c !== 0) withBalance++;
        if (c > 0) { owed += c; inDebit++; } else if (c < 0) { held += -c; inCredit++; }
        dBills += toPaise(a.debitBills);
        cBills += toPaise(a.creditBills);
        const u = toPaise(a.unbilled);
        if (u > 0) { if (a.unbilledReason === 'not_bill_wise') dNotBillWise += u; else dOnAccount += u; }
        if (u < 0) { if (a.unbilledReason === 'not_bill_wise') cNotBillWise += -u; else cOnAccount += -u; }
        for (const b of a.bills) {
            if (b.side === 'Cr') { creditN++; continue; }
            debitN++;
            const p = toPaise(b.pending);
            agedN++;
            if (b.placement?.kind === 'bucket') { bucketP[b.placement.bucket] = (bucketP[b.placement.bucket] ?? 0) + p; bucketN[b.placement.bucket] = (bucketN[b.placement.bucket] ?? 0) + 1; }
            else if (b.placement?.kind === 'not_yet_due') { nyd += p; nydN++; }
            else { unagedNoDue += p; unagedNoDueN++; }
            if (b.overdueForCollections) { overdue += p; overdueN++; }
            if (b.withinGrace) { grace += p; graceN++; }
        }
    }
    const byGroup = new Map<string, { group: string; accounts: number; net: number }>();
    for (const a of excluded) {
        if (a.excludedBy?.kind !== 'group') continue;
        const e = byGroup.get(a.excludedBy.group) ?? { group: a.excludedBy.group, accounts: 0, net: 0 };
        byGroup.set(a.excludedBy.group, { group: e.group, accounts: e.accounts + 1, net: fromPaise(toPaise(e.net) + toPaise(a.closing)) });
    }
    const notBillWise = dNotBillWise - cNotBillWise;
    const onAccount = dOnAccount - cOnAccount;
    return {
        accounts: { read: accounts.length, withBalance, inScope: inScope.length, excluded: excluded.length, inDebit, inCredit },
        owedToYou: fromPaise(owed),
        heldForCustomers: fromPaise(held),
        net: fromPaise(owed - held),
        composition: {
            debitBills: fromPaise(dBills), debitNotBillWise: fromPaise(dNotBillWise), debitOnAccount: fromPaise(dOnAccount),
            creditBills: fromPaise(cBills), creditOnAccount: fromPaise(cOnAccount), creditNotBillWise: fromPaise(cNotBillWise),
        },
        ageing: {
            basis: settings.ageing.basis,
            labels,
            buckets: labels.map((label, i) => ({ label, amount: fromPaise(bucketP[i]!), bills: bucketN[i]! })),
            notYetDue: { amount: fromPaise(nyd), bills: nydN },
            unagedNoDueDate: { amount: fromPaise(unagedNoDue), bills: unagedNoDueN },
            aged: { amount: fromPaise(dBills), bills: agedN },
        },
        unaged: { notBillWise: fromPaise(notBillWise), onAccount: fromPaise(onAccount), total: fromPaise(notBillWise + onAccount) },
        overdueForCollections: { amount: fromPaise(overdue), bills: overdueN },
        withinGrace: { amount: fromPaise(grace), bills: graceN },
        graceDays: settings.graceDays,
        excluded: {
            accounts: excluded.length,
            net: fromPaise(excluded.reduce((s, a) => s + toPaise(a.closing), 0)),
            byGroup: [...byGroup.values()].sort((a, b) => a.group.localeCompare(b.group)),
            byAccount: excluded.filter(a => a.excludedBy?.kind === 'account').map(a => ({ sourceKey: a.sourceKey, name: a.name, net: a.closing })),
        },
        unmatchedBills: { bills: unmatched.length, amount: fromPaise(unmatched.reduce((s, b) => s + toPaise(b.pending), 0)) },
        bills: { debit: debitN, credit: creditN },
    };
}

/** The groups the ledgers sit in, with how many ledgers and how much — for choosing what is in scope. */
export function groupsOf(facts: Pick<SourceFacts, 'accounts'>): { group: string; accounts: number; net: number }[] {
    const m = new Map<string, { group: string; accounts: number; netP: number }>();
    for (const a of facts.accounts) {
        const k = a.group.trim().toLowerCase();
        const e = m.get(k) ?? { group: a.group.trim(), accounts: 0, netP: 0 };
        m.set(k, { group: e.group, accounts: e.accounts + 1, netP: e.netP + toPaise(a.closing) });
    }
    return [...m.values()].map(g => ({ group: g.group, accounts: g.accounts, net: fromPaise(g.netP) })).sort((a, b) => a.group.localeCompare(b.group));
}
